import { X, ExternalLink, AlertTriangle } from 'lucide-react';
import type { QueryResultSet } from '../../../../types/queryExecution';
import { formatRowCount } from '../../common/utils/formatRowCount';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
type PeekResultsState =
  | { status: 'loading' }
  | {
      status: 'success';
      resultSet: QueryResultSet;
      query: string;
      /**
       * True when the peek hit its row cap — either the engine reported the
       * result was truncated, or our `wrapWithLimit` LIMIT was reached. The
       * panel surfaces this with a header banner so the user knows they need
       * to run the query for the full result set.
       */
      isCapped: boolean;
      /** Cap applied to the peek (rows actually fetched, e.g. 50). */
      cappedRowLimit: number;
    }
  | { status: 'error'; message: string };

interface PeekResultsPanelProps {
  state: PeekResultsState;
  onClose: () => void;
  onOpenInResults?: (query: string) => void;
  /**
   * Pointer-down on the header bar — wired in from `PeekResultsOverlay` to
   * start a drag of the floating panel. The panel itself is position-agnostic
   * (it just renders); the overlay tracks the user-set position.
   */
  onHeaderPointerDown?: (e: React.PointerEvent<HTMLDivElement>) => void;
}

/**
 * Compact panel rendered as a fixed-positioned popup by `PeekResultsOverlay`.
 * Header bar doubles as the drag handle (when `onHeaderPointerDown` is wired
 * in by the overlay). Body shows peek query results in a simple table
 * (max 50 rows, 300px scrollable height) plus an optional "capped" banner
 * when the row cap was hit.
 */
export function PeekResultsPanel({ state, onClose, onOpenInResults, onHeaderPointerDown }: PeekResultsPanelProps) {
  return (
    <div
      className="border border-border bg-background rounded-md shadow-lg overflow-hidden"
      data-testid="peek-results-panel"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header — also acts as the drag handle. Buttons inside set
          `data-peek-no-drag` so the overlay's drag handler can tell a
          drag-from-header apart from a click on Close / Open in results. */}
      <div
        className={`flex items-center justify-between px-3 py-1.5 bg-muted/50 border-b border-border select-none ${onHeaderPointerDown ? 'cursor-move' : ''}`}
        onPointerDown={onHeaderPointerDown}
        data-testid="peek-results-header"
      >
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {state.status === 'loading' && (
            <>
              <RandomDotmSpinner size={12} />
              <span>Executing peek query...</span>
            </>
          )}
          {state.status === 'success' && (
            <span className="font-medium tabular-nums">
              {formatRowCount(state.resultSet.rowCount ?? state.resultSet.rows.length)}
            </span>
          )}
          {state.status === 'error' && (
            <span className="text-destructive font-medium">Error</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {state.status === 'success' && onOpenInResults && (
            <button
              onClick={() => onOpenInResults(state.query)}
              className="flex items-center gap-1 px-2 py-0.5 text-xs text-muted-foreground
                hover:text-foreground hover:bg-muted rounded transition-colors"
              title="Open in results"
              data-testid="peek-open-in-results"
            >
              <ExternalLink className="h-3 w-3" />
              Open in results
            </button>
          )}
          <button
            onClick={onClose}
            className="p-0.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
            title="Close peek"
            data-testid="peek-close-button"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Body */}
      {state.status === 'loading' && (
        <div className="h-16 flex items-center justify-center">
          <RandomDotmSpinner size={20} className="text-muted-foreground" />
        </div>
      )}

      {state.status === 'error' && (
        <div className="px-3 py-2 text-xs text-destructive whitespace-pre-wrap max-h-[100px] overflow-auto">
          {state.message}
        </div>
      )}

      {state.status === 'success' && state.isCapped && (
        <div
          className="flex items-start gap-1.5 px-3 py-1.5 text-[11px]
            bg-amber-500/10 border-b border-amber-500/30 text-amber-900 dark:text-amber-200"
          data-testid="peek-results-capped-banner"
        >
          <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
          <span>
            Showing only the first {state.cappedRowLimit} rows. Execute the query for the full result set.
          </span>
        </div>
      )}

      {state.status === 'success' && (
        <div className="max-h-[300px] overflow-auto">
          <table className="w-full text-xs border-collapse">
            <thead className="sticky top-0 bg-muted/80 backdrop-blur-sm">
              <tr>
                {state.resultSet.columns.map((col, i) => (
                  <th
                    key={col.id ?? col.name ?? i}
                    className="px-2 py-1 text-left font-medium text-muted-foreground
                      border-b border-r border-border whitespace-nowrap"
                  >
                    {col.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {state.resultSet.rows.map((row, rowIdx) => (
                <tr key={rowIdx} className="hover:bg-muted/30">
                  {state.resultSet.columns.map((col, colIdx) => {
                    const key = col.id ?? col.name;
                    const val = row[key];
                    return (
                      <td
                        key={colIdx}
                        className="px-2 py-0.5 border-b border-r border-border/50
                          whitespace-nowrap max-w-[200px] truncate"
                        title={val == null ? 'NULL' : String(val)}
                      >
                        {val == null ? (
                          <span className="text-muted-foreground/50 italic">NULL</span>
                        ) : (
                          String(val)
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export type { PeekResultsState };
